<script>
import Layout from '@layouts/adminLayout.vue'
import DebugLayout from '@layouts/debugLayout.vue'
import MottoIcon from '@components/motto-icon.vue'
import LoaderIcon from '@components/loader-icon.vue'
import LeaderboardPosition from '@components/leaderboards/leaderboard-position.vue'
import { getMyRankings, getMyRankingsDebug } from '@src/api/leaderboards'
import { transformManyRankingData as transformRankingData } from '@src/api/transformers/leaderboardTransformer'
import { getDateObjectFromPeriodString, getYearAndMonthStringFromPeriodString } from '@utils/date-helpers'
import _ from 'lodash'
import { authComputed } from '@state/helpers'

export default {
  page: {
    title: 'My Rankings',
    meta: [
      {
        name: 'description',
        content: 'The My Rankings page.',
      },
    ],
  },
  components: {
    Layout,
    LoaderIcon,
    DebugLayout,
    MottoIcon,
    LeaderboardPosition,
  },
  data() {
    return {
      loading: true,
      error: false,
      myRankings: [],
      myRankingFetchedUserId: null,
      selectedLeaderboardCategory: 'All',
      debugMode: this.$attrs['debug-mode'] === 'yes',
      debugUserId: this.$attrs['debug-user-id'],
    }
  },
  computed: {
    ...authComputed,
    getMyRankingsData() {
      const orderedMyRakingKeys = _(this.myRankings)
        .toPairs()
        .orderBy(([periodString]) => getDateObjectFromPeriodString(periodString), ['desc'])
        .fromPairs()
        .value()
      return _.mapKeys(orderedMyRakingKeys, (...[, key]) => {
        const { month, year } = getYearAndMonthStringFromPeriodString(key)
        return `${month} ${year}`
      })
    },

    isRankingEmpty() {
      return _.isEmpty(this.myRankings)
    },

    getLeaderboardCategories() {
      const allKeys = _.values(this.getMyRankingsData).map((x) => _.keys(x))
      const uniqKeys = _(allKeys).flatten().uniq().value()
      return uniqKeys.sort()
    },
  },
  created() {
    this.fetchMyRankings({ debugUser: this.debugMode })
  },
  methods: {
    async fetchMyRankings({ debugUser = false }) {
      const myRankingsRequest = debugUser
        ? () => {
            return getMyRankingsDebug(this.debugUserId)
          }
        : getMyRankings
      try {
        const {
          data: { data: myRankingData },
        } = await myRankingsRequest()
        this.myRankingFetchedUserId = myRankingData.userIdFetched
        this.myRankings = transformRankingData(myRankingData)
      } catch (err) {
        this.error = true
      } finally {
        this.loading = false
      }
    },
    isMyRank(leaderboardData) {
      return (
        leaderboardData.userId === this.currentUser.id ||
        this.myRankingFetchedUserId === leaderboardData.userId ||
        leaderboardData.isWorkingLocation
      )
    },
    selectLeaderboardCategory(leaderboardCategory) {
      this.selectedLeaderboardCategory = leaderboardCategory
    },
    filterPeriodLeaderboardBySelectedLeaderboardCategory(periodLeaderboard) {
      if (this.selectedLeaderboardCategory === 'All') {
        return periodLeaderboard
      }
      return _.pick(periodLeaderboard, this.selectedLeaderboardCategory)
    },
  },
}
</script>

<template>
  <component :is="debugMode ? 'DebugLayout' : 'Layout'">
    <div :class="$style.viewContainer">
      <LoaderIcon v-if="loading || error" :error="error"></LoaderIcon>
      <div v-else>
        <div :class="$style.breadcrumbs">
          <RouterLink :to="{ name: 'leaderboards' }">
            <MottoIcon :class="$style.backButton" name="Arrow" icon-color="gray" class="cursor-pointer" />
          </RouterLink>
          <RouterLink :to="{ name: 'leaderboards' }">
            <p class="p2 color-darkest-gray cursor-pointer">LEADERBOARDS</p>
          </RouterLink>
          <BaseIcon class="color-gray" name="chevron-right" />
          <p :class="$style.selectedBreadcrumb" class="button-text color-primary-blue">MY RANKINGS</p>
        </div>
        <div class="d-flex mb-30 justify-content-between align-items-center">
          <h2 :class="$style.myRankingTitle" class="color-primary-blue mb-0">My Rankings</h2>
          <BaseSelect
            v-if="!isRankingEmpty"
            :class="$style.leaderboardCategoriesSelect"
            name="leaderboardCategory"
            :options="['All', ...getLeaderboardCategories]"
            data-my-rankings-leaderboard-select
            @update="selectLeaderboardCategory" />
        </div>
        <div v-if="isRankingEmpty">
          <div class="text-center">
            <h1 class="color-primary-blue">Keep Going!</h1>
            <h4 class="color-primary-blue">Once you close your first Motto loan, you'll be ranked in the Network!</h4>
          </div>
        </div>
        <div v-if="!isRankingEmpty" ref="gridContainer" :class="$style.myRankingsContainer">
          <div v-for="(periodLeaderboard, period, index) in getMyRankingsData" :key="index" class="d-contents">
            <div
              v-for="(
                leaderboardDataList, leaderboardCategoryName, indexPeriodLeaderboard
              ) in filterPeriodLeaderboardBySelectedLeaderboardCategory(periodLeaderboard)"
              :key="indexPeriodLeaderboard"
              data-my-rankings-block>
              <div :class="$style.myRankingsBlockHeader" class="d-flex justify-content-between">
                <h3 class="color-primary-blue">{{ leaderboardCategoryName }}</h3>
                <h4 data-my-ranking-block-period>{{ period }}</h4>
              </div>
              <LeaderboardPosition
                v-for="(leaderboardData, leaderboardDataIndex) in leaderboardDataList"
                :key="leaderboardDataIndex"
                :leaderboard-data="leaderboardData"
                :class="$style.leaderboardPosition"
                :leaderboard-position="leaderboardData.rank"
                :highlight="isMyRank(leaderboardData)"
                :small-theme="true" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </component>
</template>

<style lang="scss" module>
@import '@design';

.viewContainer {
  width: auto;
}

.breadcrumbs {
  display: flex;
  align-items: center;
  margin-bottom: 40px;

  > svg {
    margin: -2px 13px 0;
  }
}

.backButton {
  margin-right: 29px;

  > svg {
    width: 30px;
    height: 30px;
  }
}

.selectedBreadcrumb {
  &::after {
    float: left;
    width: 100%;
    height: 4px;
    content: '';
    background: $primary-blue;
    border-radius: 5px;
  }
}

.myRankingTitle {
  @media (max-width: $lg-breakpoint) {
    margin-right: 20px;
    font-size: 18px;
    white-space: nowrap;
  }
}

.leaderboardCategoriesSelect {
  @media (max-width: $lg-breakpoint) {
    width: auto;
  }

  width: 302px;
}

.myRankingsContainer {
  @media (max-width: $lg-breakpoint) {
    grid-template-columns: 1fr;
  }

  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(411px, 1fr));
  grid-gap: 56px 72px;
  margin-top: 0;
}

.leaderboardPosition {
  margin-top: 16px;
}

.myRankingsBlockHeader {
  h3 {
    @media (max-width: $lg-breakpoint) {
      font-size: 14px;
    }
  }

  h4 {
    @media (max-width: $lg-breakpoint) {
      font-size: 14px;
    }
  }
}
</style>
